import Select, { SelectOption } from 'components/FormUtils/Select'
import { AiOutlineWarning } from 'react-icons/ai'

import {
  ContactResponse,
  DepartmentLabels,
  SeniorityLabels,
} from '../../../models/contacts'
import { CompanyAutocomplete } from '../../CompanyAutocomplete'
import lodash from 'lodash'
import { CreatableTagSelector } from '../../CreatableTagSelector'
import { Checkbox } from '../../FormUtils/Checkbox'
import CreatableSelect from '../../FormUtils/CreatableSelect'
import { ErrorLabel } from '../../FormUtils/ErrorLabel'
import { PhoneInput } from '../../FormUtils/PhoneInput'
import { TextareaInput } from '../../FormUtils/TextareaInput'
import { TextInput } from '../../FormUtils/TextInput'
import { FormSelect } from '../../UI/Select/FormSelect'
import * as S from './ContactModal.styles'
import { useFormContext, useWatch } from 'react-hook-form'
import { ContactDefaultValues } from './ContactModal'
import useApiService from 'services/api'
import { useEffect, useMemo } from 'react'
import { useImmer } from 'use-immer'
import { ContactCompany, ContactCompanyCreateRequest } from 'models/companies'
import { CONTACT_TITLES } from 'utils/constants'

export default function ContactDetailsStep({
  forCompany,
  contactToEdit,
}: {
  forCompany?: Partial<ContactCompany>
  contactToEdit?: ContactResponse
}) {
  const {
    control,
    setValue,
    trigger,
    setError,
    watch,
    formState: { errors },
  } = useFormContext<ContactDefaultValues>()

  const api = useApiService()
  const { data: categories } = api.useGetCompanyCategories()
  const { data: tagsOptions } = api.useGetTagsOptions(false, 'contacts')
  const companyCategories = useMemo(() => categories || [], [categories])

  const [warnings, setWarnings] = useImmer<{
    email_work?: string
    email_personal?: string
    email_generic?: string
    company_domain?: string
  }>({})

  const initialCompanyData = useMemo(() => {
    if (!companyCategories)
      return {
        company: undefined,
        categoryData: undefined,
        subCategoryData: undefined,
      }
    const contact_company = forCompany ?? contactToEdit?.contact_company
    if (!contact_company)
      return {
        company: undefined,
        categoryData: undefined,
        subCategoryData: undefined,
      }
    const categoryData = companyCategories.find(
      (category) => category.name === contact_company?.category
    )
    const subCategoryData = categoryData?.sub_categories.find(
      (subCategory) => subCategory.name === contact_company?.sub_category?.name
    )

    const clonedCompany = lodash.cloneDeep(contact_company)

    const company = {
      ...clonedCompany,
      category: categoryData?.name,
      sub_category: subCategoryData,
    } as ContactCompany

    return { company, categoryData, subCategoryData }
  }, [contactToEdit, forCompany, companyCategories])

  const initialCompany = initialCompanyData.company

  useEffect(() => {
    // Fill in form with existing contact data
    if (!initialCompany) return
    setValue('contact_company', initialCompany as ContactCompanyCreateRequest, {
      shouldValidate: false,
      shouldDirty: false,
      shouldTouch: false,
    })
    setValue('company_domain', initialCompany?.domain ?? '', {
      shouldValidate: false,
      shouldDirty: false,
      shouldTouch: false,
    })
    setValue('category_id', initialCompanyData.categoryData?.id, {
      shouldValidate: false,
      shouldDirty: false,
      shouldTouch: false,
    })
    setValue(
      'sub_category_id',
      initialCompany?.sub_category
        ? initialCompany.sub_category?.id
        : undefined,
      {
        shouldValidate: false,
        shouldDirty: false,
        shouldTouch: false,
      }
    )
  }, [initialCompany, initialCompanyData.categoryData])

  const watchCompany = watch('contact_company')

  const isNewCompany = useMemo(() => {
    const contact_company = watchCompany
    // If contact_company is not defined, or it's an empty object, it's not a new contact_company
    if (!contact_company || Object.keys(contact_company).length === 0)
      return false
    return contact_company?.id === -1
  }, [watchCompany])

  useEffect(() => {
    const contact_company = watchCompany
    if (contact_company?.domain) {
      setValue('company_domain', contact_company?.domain)
      trigger('company_domain')
    } else if (!contact_company) {
      setValue('company_domain', '')
    }
    // The contact_company from CompanyAutocomplete has these fields embedded
    // We need to extract the IDs from the nested structure
    if (contact_company && 'category_id' in contact_company) {
      setValue('category_id', (contact_company as any).category_id)
    }
    if (contact_company && 'sub_category_id' in contact_company) {
      setValue('sub_category_id', (contact_company as any).sub_category_id)
    } else if (
      contact_company?.sub_category &&
      typeof contact_company.sub_category === 'object' &&
      'id' in contact_company.sub_category
    ) {
      setValue('sub_category_id', (contact_company.sub_category as any).id)
    }
  }, [watchCompany])

  const watchCompanyAndDomain = useWatch({
    control,
    name: ['contact_company', 'company_domain'],
  })

  useEffect(() => {
    const existingCompanyWithNoDomainWarning = (
      contact_company: ContactCompany,
      company_domain?: string
    ) => {
      const isNewCompany = !(contact_company?.id || contact_company?.chain)
      if (!isNewCompany && !company_domain && !forCompany) {
        setWarnings((draft) => {
          draft.company_domain =
            'Missing domain. Add a domain to ensure accuracy across systems.'
        })
        return true
      }
      return false
    }

    const companyDomainBeingOverwrittenWarning = (
      contact_company: ContactCompany,
      company_domain?: string
    ) => {
      const bothDomainsDefined = contact_company?.domain && company_domain
      if (bothDomainsDefined) {
        if (contact_company.domain !== company_domain) {
          setWarnings((draft) => {
            draft.company_domain =
              'Domain does not match selected company. Match domains to ensure accuracy across systems, or continue to override.'
          })
          return true
        }
      }
      return false
    }

    if (errors.company_domain) return

    const contact_company = watchCompanyAndDomain[0] as ContactCompany
    const company_domain = watchCompanyAndDomain[1]

    if (companyDomainBeingOverwrittenWarning(contact_company, company_domain))
      return

    if (existingCompanyWithNoDomainWarning(contact_company, company_domain))
      return

    setWarnings((draft) => {
      delete draft.company_domain
    })
  }, [watchCompanyAndDomain, errors, forCompany])

  const categoryOptions =
    companyCategories?.map((category) => ({
      label: category.name,
      value: category.id,
    })) || []

  const watchCompanyCategory = watch('category_id')

  const typeOptions = useMemo(() => {
    const category = watchCompanyCategory
    if (!category) return []

    const categoryObj = companyCategories?.find((c) => c.id === category)
    if (!categoryObj) return []

    return categoryObj.sub_categories.map(
      (subCategory) =>
        ({
          label: subCategory.name,
          value: subCategory.id,
        }) as SelectOption
    )
  }, [watchCompanyCategory])

  const createNewCategory = (
    category: string
  ): Promise<{
    label: string
    value: string
  }> => {
    return new Promise((resolve) => {
      resolve({
        label: category,
        value: category,
      })
    })
  }

  const contactTitles = useMemo(() => {
    const defaultTitles = [...CONTACT_TITLES]
    if (defaultTitles.includes(contactToEdit?.title as string)) {
      return defaultTitles
    }
    return [...defaultTitles, contactToEdit?.title as string]
  }, [contactToEdit?.title])

  return (
    <>
      <div className={'flex items-start w-full gap-2'}>
        <TextInput
          label={'First Name'}
          control={control}
          name="first_name"
          placeholder="First Name"
        />
        <TextInput
          control={control}
          name="last_name"
          placeholder="Last Name"
          label={'Last Name'}
        />
      </div>
      <div className={'md:flex items-start w-full gap-2'}>
        <CompanyAutocomplete
          disabled={!!forCompany}
          control={control}
          name="contact_company"
          placeholder="Start typing company name"
          className={'mt-[10px]'}
          height={'33px'}
          initialCompany={initialCompany}
        />
        <div className={'flex-1'}>
          <TextInput
            disabled={!!forCompany}
            label={'Company domain'}
            control={control}
            name="company_domain"
            placeholder="company.com"
            className={`h-[33px] ${warnings.company_domain ? 'input-warning' : ''}`}
          />
        </div>
        {(warnings.email_work ||
          warnings.email_personal ||
          warnings.email_generic) && (
          <S.Warning>
            <AiOutlineWarning size={20} />
            <div>
              {warnings.email_work ||
                warnings.email_personal ||
                warnings.email_generic}
            </div>
          </S.Warning>
        )}
      </div>
      {warnings.company_domain && (
        <S.Warning>
          <AiOutlineWarning size={20} />
          <div>{warnings.company_domain}</div>
        </S.Warning>
      )}
      {isNewCompany && (
        <div>
          <div className="mt-3 mb-1 text-base font-medium text-gray-800 select-none">
            Company Category and Type
          </div>
          <div className="flex items-start gap-2">
            <div className="md:flex-1 w-full">
              <FormSelect
                control={control}
                name="category_id"
                buttonLabel="Company Category"
                items={categoryOptions}
                buttonClassName="w-full"
              />
              {/* Select only colors its (closed) panel border on error, so
                  the required-field messages for a new company never render
                  on their own. Surface them here so submitting an incomplete
                  new company shows why Create is blocked instead of silently
                  doing nothing. */}
              {errors.category_id?.message && (
                <ErrorLabel message={errors.category_id.message} />
              )}
            </div>
            <div className="flex-1 w-full">
              <FormSelect
                control={control}
                name="sub_category_id"
                buttonLabel="Company Type"
                items={typeOptions}
                buttonClassName="w-full"
              />
              {errors.sub_category_id?.message && (
                <ErrorLabel message={errors.sub_category_id.message} />
              )}
            </div>
          </div>
        </div>
      )}
      <div className="flex flex-col gap-2">
        <div className="md:flex items-start gap-2">
          <TextInput
            label="Work Email"
            control={control}
            name="email_work"
            placeholder="work@company.com"
            className={warnings.email_work ? 'input-warning' : undefined}
          />
          <TextInput
            label="Personal Email"
            control={control}
            name="email_personal"
            placeholder="personal@example.com"
            className={warnings.email_personal ? 'input-warning' : undefined}
          />
          <TextInput
            label="Other Email"
            control={control}
            name="email_generic"
            placeholder="info@company.com"
            className={warnings.email_generic ? 'input-warning' : undefined}
          />
        </div>
        <div className="text-sm italic text-gray-600">
          At least one email is required
        </div>
        {(warnings.email_work ||
          warnings.email_personal ||
          warnings.email_generic) && (
          <S.Warning>
            <AiOutlineWarning size={20} />
            <div>
              {warnings.email_work ||
                warnings.email_personal ||
                warnings.email_generic}
            </div>
          </S.Warning>
        )}
      </div>

      <PhoneInput
        name="phone_number"
        control={control}
        label="Phone number"
        extensionName="extension"
        optional
      />
      <div className={'md:flex items-start gap-2'}>
        <TextInput
          control={control}
          name="url"
          label="Website"
          placeholder="https://www.example.com/"
          optional
        />
        <TextInput
          control={control}
          name="linkedin_url"
          label="LinkedIn"
          placeholder="https://www.linkedin.com/"
          optional
        />
      </div>
      <div>
        <div className="text-lg mt-3 font-medium text-gray-800">
          Contact Type
        </div>
        <div className={'md:flex items-start gap-2'}>
          <CreatableSelect
            label={'Title'}
            optional
            control={control}
            name="title"
            placeholder="Title"
            options={contactTitles.map((title) => ({
              label: title,
              value: title,
            }))}
            onCreateOption={createNewCategory}
            className={'flex-1 mt-0'}
          />
          <CreatableTagSelector
            className={'flex-1 mt-0'}
            name="tags"
            control={control}
            placeholder="Labels"
            options={tagsOptions}
            setError={setError}
            tagParent="contacts"
            label={'Labels'}
            optional
          />
        </div>
      </div>
      {!!contactToEdit && (
        <>
          <div className={'md:flex items-start gap-2'}>
            <Select
              className={'flex-1'}
              control={control}
              name="seniority"
              label="Seniority"
              placeholder="Select Seniority"
              options={[
                { value: null, label: 'Select Seniority' },
                ...Object.entries(SeniorityLabels).map(([value, label]) => ({
                  value: parseInt(value),
                  label,
                })),
              ]}
              optional
            />
            <Select
              className={'flex-1'}
              control={control}
              name="department"
              label="Department"
              placeholder="Select Department"
              options={[
                { value: null, label: 'Select Department' },
                ...Object.entries(DepartmentLabels).map(([value, label]) => ({
                  value: parseInt(value),
                  label,
                })),
              ]}
              optional
            />
          </div>
        </>
      )}
      <TextareaInput
        control={control}
        name="notes"
        label="Notes"
        placeholder="Notes"
        optional
      />
      <Checkbox
        control={control}
        name="consent_to_contact"
        wrapperStyle={{ paddingTop: '8px' }}
      >
        Do you have consent to contact this person?{' '}
        <strong>You need to confirm this before proceeding.</strong>
      </Checkbox>
    </>
  )
}
